import React, { useEffect, useMemo, useRef, useState } from 'react';

import { PLANNING_STATUS } from '@features/order/utils/projectUtil';

import tnaService from '@services/tna-service';

import { STATIC_SET_CONSTANTS } from '@utils/Util';

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;

const PlanSummary = ({ plan }) => {
	const orderLabel = [plan?.orderName, plan?.orderRefNo && `(${plan.orderRefNo})`]
		.filter(Boolean)
		.join(' ');
	const codes = [plan?.skuNo && `SKU: ${plan.skuNo}`, plan?.poNumber && `PO: ${plan.poNumber}`]
		.filter(Boolean)
		.join(' | ');

	return (
		<div className="d-flex align-items-center">
			<img
				src={plan?.imageUrl || `${process.env.PUBLIC_URL}/icons/image-default-bg.svg`}
				alt="design"
			/>
			<div className="ml-2">
				<p className="regular-12 gray_dark_01 weight-500 mb-0">
					{plan?.styleRefNo || plan?.styleName || 'Unnamed style'}
				</p>
				{orderLabel && <p className="regular-12 gray_dark_02 mb-0">{orderLabel}</p>}
				{codes && <p className="regular-12 gray_dark_02 mb-0">{codes}</p>}
			</div>
		</div>
	);
};

// The styles of this order whose own TNA is done, shown before anything is typed. A style of a set
// is listed in its own right, because each member is planned separately.
const getPlansOfThisOrder = (orderId, tnaProductList = []) => {
	const plans = [];
	const seenProductIds = new Set();

	const addStyle = (style, parent) => {
		if (style?.planningStatus === PLANNING_STATUS.PENDING || seenProductIds.has(style?.id)) return;

		seenProductIds.add(style?.id);
		plans.push({
			orderId,
			rfqId: style?.rfqId,
			productId: style?.id,
			styleRefNo: style?.referenceNumber,
			styleName: style?.name,
			orderName: style?.orderTitle || parent?.orderTitle,
			orderRefNo: style?.orderReferenceNumber || parent?.orderReferenceNumber,
			poNumber: style?.poNumber || parent?.poNumber,
			imageUrl: style?.image
		});
	};

	(tnaProductList || []).forEach((style) => {
		if (style?.setPackStyleFlag === STATIC_SET_CONSTANTS.SET_PRODUCT && style?.setMemberList) {
			style.setMemberList.forEach((member) => addStyle(member, style));
			return;
		}
		addStyle(style);
	});

	return plans;
};

// A TNA can be copied from any style whose own TNA is done. This order's styles are there to pick
// straight away; a style on another order is found by searching for its style number, SKU number
// or order.
const CreateDuplicateTNA = ({
	orderId,
	tnaProductList = [],
	onSelectPlanForDuplication,
	isExcelFileSubmitted
}) => {
	const [search, setSearch] = useState('');
	const [plans, setPlans] = useState([]);
	const [searching, setSearching] = useState(false);
	const latestRequest = useRef(0);

	const searchTerm = search.trim();
	const isSearching = searchTerm.length >= MIN_SEARCH_LENGTH;

	const plansOfThisOrder = useMemo(
		() => getPlansOfThisOrder(orderId, tnaProductList),
		[orderId, tnaProductList]
	);

	useEffect(() => {
		if (!isSearching) {
			setPlans([]);
			setSearching(false);
			return;
		}

		setSearching(true);
		const requestId = ++latestRequest.current;
		const timer = setTimeout(async () => {
			const result = await tnaService.searchDuplicablePlans(searchTerm);
			// Typing carries on while a search is in flight, so a slower earlier one must not
			// overwrite the newest result.
			if (requestId !== latestRequest.current) return;

			setPlans(result || []);
			setSearching(false);
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [searchTerm, isSearching]);

	const renderMessage = (message) => (
		<li className="no-hover-row">
			<p className="regular-12 gray_dark_02 mb-0">{message}</p>
		</li>
	);

	const renderPlans = (planList) =>
		planList.map((plan) => (
			<li
				key={`plan-${plan?.orderId}-${plan?.rfqId}-${plan?.productId}`}
				className="cursor-pointer"
				onClick={() => onSelectPlanForDuplication(plan)}
			>
				<PlanSummary plan={plan} />
			</li>
		));

	return (
		<React.Fragment>
			<div className="single-filter">
				<div className="duplicate-filter">
					<div className="dropdown">
						<button
							className="btn btn-default dropdown-toggle"
							type="button"
							data-toggle="dropdown"
							disabled={isExcelFileSubmitted()}
						>
							<span>Duplicate</span>
						</button>
						<ul className="dropdown-menu duplicate-search">
							<li className="no-hover-row search-row">
								<input
									type="text"
									placeholder="Search style no, SKU no or order"
									autoComplete="off"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
								/>
							</li>
							{!isSearching && plansOfThisOrder.length > 0 && renderPlans(plansOfThisOrder)}
							{!isSearching &&
								plansOfThisOrder.length === 0 &&
								renderMessage(
									'No style on this order has a created TNA yet. Search for one on another order.'
								)}
							{isSearching && searching && renderMessage('Searching...')}
							{isSearching &&
								!searching &&
								plans.length === 0 &&
								renderMessage('No style with a created TNA matches that.')}
							{isSearching && !searching && renderPlans(plans)}
						</ul>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

export default CreateDuplicateTNA;
